import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ErrorDisplay } from '../shared/Display';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

interface SupplierGuardProps {
  children: React.ReactNode;
  showFallback?: boolean;
}

export const SupplierGuard: React.FC<SupplierGuardProps> = ({ 
  children, 
  showFallback = true 
}) => {
  const { user, isLoading } = useAuth();

  // Still loading user data
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // User is not authenticated or not a supplier
  if (!user || user.userType !== 'Supplier') {
    if (!showFallback) {
      return null;
    }

    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="max-w-md w-full">
          <ErrorDisplay 
            message="Access restricted to supplier accounts only"
          />
          <div className="mt-6 text-center">
            <InformationCircleIcon className="w-12 h-12 text-blue-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Supplier Portal Access
            </h3>
            <p className="text-gray-600 mb-4">
              This page is only accessible to registered supplier accounts. 
              If you believe you should have access, please contact your account manager.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <InformationCircleIcon className="w-5 h-5 text-blue-400 mt-0.5" />
                <div className="ml-3 text-sm text-blue-700">
                  <p className="font-medium">Need help?</p>
                  <p>Contact support for assistance with your supplier account setup.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};